import React from "react"
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useTranslation } from "react-i18next"
import * as styles from "./PdfBuilder.module.scss"
import { ArtworkRow, AttachmentRow, PageThumb } from "./cells"
import { useBuilder } from "./context"

/** Main scrollable pane: cover + per-artwork page composition. */
export const CompositionPane: React.FC = () => {
	const { state, dispatch } = useBuilder()
	const { t } = useTranslation()
	// drag listeners live on the dedicated handle button, so the plain
	// PointerSensor is correct here (CustomPointerSensor blocks activation
	// from buttons, which is exactly where our handle lives)
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) {
			return
		}
		const from = state.contentRows.findIndex((row) => row.id === active.id)
		const to = state.contentRows.findIndex((row) => row.id === over.id)
		if (from !== -1 && to !== -1) {
			dispatch({ type: "moveRow", from, to })
		}
	}

	return (
		<div className={styles.main}>
			<div className={styles.mainContent}>
				<div className={styles.section}>
					<h3 className={styles.sectionTitle}>{t("pdfBuilder.cover")}</h3>
					<div className={styles.coverRow}>
						{state.coverRow.pages.map((page) => (
							<PageThumb
								key={page.id}
								page={page}
								customInput={state.coverRow.customInput}
								onToggle={() => dispatch({ type: "togglePage", rowId: state.coverRow.id, pageId: page.id })}
							/>
						))}
						<div className={styles.coverTitleField}>
							<label className={styles.inputLabel} htmlFor="pdf-cover-title">
								{t("pdfBuilder.titleLabel")}
							</label>
							<input
								id="pdf-cover-title"
								type="text"
								className={styles.textInput}
								value={state.coverRow.customInput}
								onChange={(e) => dispatch({ type: "setCoverTitle", title: e.target.value })}
							/>
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<h3 className={styles.sectionTitle}>{t("pdfBuilder.pages")}</h3>
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
						<SortableContext items={state.contentRows.map((row) => row.id)} strategy={verticalListSortingStrategy}>
							{state.contentRows.map((row, index) =>
								row.rowType === "attachment" ? (
									<AttachmentRow key={row.id} row={row} index={index} />
								) : (
									<ArtworkRow key={row.id} row={row} index={index} />
								)
							)}
						</SortableContext>
					</DndContext>
				</div>
			</div>
		</div>
	)
}
